import React, { useRef } from "react";

import { Button, Modal } from "@amphore-dev/amphore-lib";
import { FormikProps } from "formik";
import { t } from "i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { TCompany, TProject, TProjectEditFormValues } from "@/types";

import { ProjectEditForm, toProjectPayload } from "@/forms/index";
import { promiseToast } from "@/modules/index";

export interface IProjectFormModalProps {
	open: boolean;
	onClose: () => void;
	onSaved?: (project: TProject) => void;
	/** Preselected in the form - the client page opens the modal on itself. */
	client?: TCompany | null;
	/** Opens on "internal" (no client) - the /projects page's internal group. Ignored with a `client`. */
	internal?: boolean;
	/** The workspace an internal project or a new client goes to - the /projects page's block. Default: the user's own. */
	workspaceId?: number;
}

/** ProjectEditForm in a modal, for the "New project" buttons. */
export const ProjectFormModal: React.FC<IProjectFormModalProps> = ({
	open,
	onClose,
	onSaved,
	client,
	internal = false,
	workspaceId,
}) => {
	const queryClient = useQueryClient();
	const formRef = useRef<FormikProps<TProjectEditFormValues>>(null);

	const mutation = useMutation({
		mutationFn: async (values: TProjectEditFormValues) =>
			ProjectsService.createProject(await toProjectPayload(values)),
		onSuccess: (saved: TProject) => {
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "views" ||
					query.queryKey[0] === "projects",
			});
			onSaved?.(saved);
			onClose();
		},
	});

	const handleSubmit = async (values: TProjectEditFormValues) => {
		const prom = mutation.mutateAsync(values);
		promiseToast(prom);
		await prom.catch(() => undefined);
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t("projects.create.title")}
			size="md"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						{t("actions.cancel")}
					</Button>
					<Button
						color="primary"
						isLoading={mutation.isPending}
						onClick={() => formRef.current?.submitForm()}
					>
						{t("projects.create.submit")}
					</Button>
				</div>
			}
		>
			{open && (
				<ProjectEditForm
					innerRef={formRef}
					withWorkspace
					initialValues={{
						...(client ? { client } : internal ? { internal } : {}),
						...(workspaceId !== undefined && { workspaceId }),
					}}
					onSubmit={handleSubmit}
				/>
			)}
		</Modal>
	);
};
